import { useState, type FormEvent } from 'react';
import { useAuthStore } from '../../store/auth-store.js';
import { useAppLockStore } from '../../store/app-lock-store.js';
import { ApiClientError } from '../../lib/api.js';

/**
 * Native-only full-screen lock overlay, shown when the 30-minute inactivity
 * window elapses inside the Android app. The session stays alive — only the
 * UI is gated. M1 ships password re-entry; M4.1 adds the biometric fast
 * path in front of it.
 */
export function AppLockScreen() {
  const isLocked = useAppLockStore((s) => s.isLocked);
  const unlock = useAppLockStore((s) => s.unlock);
  const user = useAuthStore((s) => s.user);
  const unlockWithPassword = useAuthStore((s) => s.unlockWithPassword);
  const logout = useAuthStore((s) => s.logout);

  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!isLocked || !user) return null;

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await unlockWithPassword(password);
      setPassword('');
      unlock();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not unlock. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    void handleSubmit(event);
  }

  function onSignOut(): void {
    void logout().finally(() => unlock());
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-gradient-to-br from-bn-800 to-bn-950 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Session locked"
    >
      <div className="w-full max-w-sm card">
        <div className="text-center">
          <img
            src="/11sigbn-logo.png"
            alt="11 Signal Battalion"
            className="mx-auto h-16 w-16 object-contain"
          />
          <h1 className="mt-3 text-lg font-semibold text-slate-900">Session locked</h1>
          <p className="mt-1 text-sm text-slate-500">
            Locked after 30 minutes of inactivity. Enter your password to continue as{' '}
            <span className="font-medium text-slate-700">{user.name}</span>.
          </p>
        </div>

        <form className="mt-5 space-y-4" onSubmit={onSubmit} noValidate>
          <div>
            <label htmlFor="app-lock-password" className="form-label">
              Password
            </label>
            <input
              id="app-lock-password"
              type="password"
              autoComplete="current-password"
              autoFocus
              required
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />
          </div>

          {error && (
            <div role="alert" className="rounded-md bg-rag-red-soft px-3 py-2 text-sm text-rag-red">
              {error}
            </div>
          )}

          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting ? 'Unlocking…' : 'Unlock'}
          </button>
        </form>

        <button
          type="button"
          onClick={onSignOut}
          className="mt-4 w-full text-center text-sm font-medium text-bn-700 hover:text-bn-600"
        >
          Sign out instead
        </button>
      </div>
    </div>
  );
}
